import loadable from '@loadable/component';
import { toCurrencyTruncated, toTruncated } from '@/utilities/formatting';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import ReportSection from '@components/ReportSection';
import ReportSubSection from '@components/ReportSubSection';
import { Leader } from '@/pages/CompanyReport/components/Management';
import ListTruncator from '@components/ListTruncator';
import Statement from '@/components/Statement';
import Description from '@/components/Statement/components/Description';
import {
  MANAGEMENT_BOARD_TENURE,
  MANAGEMENT_CEO,
  MANAGEMENT_CEO_SALARY,
  MANAGEMENT_TENURE,
} from '@/constants/anchors';
import PageAnchor from '@components/PageAnchor';
import type { ViewMode } from '@/pages/CompanyReport/enum';
import type { People } from '@/pages/CompanyReport/redux/interface';
import { BrazeContentCardWrapper } from '@/pages/CompanyReport/components/BrazeContentCard';
import CEOCompensationTable from './components/CEOCompensationTable';
import { COMPANY_CEO_COMPENSATION } from '@/constants/feedbackSections';
import { getCompanyInfographicDownloadUrl } from '@/utilities/urlGenerator';
import { ChartType } from '@/constants/downloadSections';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import { useVisibleSectionsContext } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import type { StatementName } from '@/hooks/useCompanyStatementsAPIFormatted/types';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import { useCompany, useCompanyCanonicalUrl } from '@/hooks/useCompany';
import { getStatementChecks } from '@/pages/CompanyReport/components/Introduction/utils';
import Introduction from '@/pages/CompanyReport/components/Introduction';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import { formatterPercentage, formatterTenure } from '@/utilities/format';
import { CompanyStatements } from '@/constants/statementIds';
import { SectionSchema } from '@/pages/CompanyReport/schemas';

const BrazeContentCard = loadable(() => import('@components/BrazeContentCard'));

const TeamTenure = loadable(
  () => import('@/pages/CompanyReport/components/Management'),
  {
    resolveComponent: (component) => component.TeamTenure,
  }
);
const TeamList = loadable(
  () => import('@/pages/CompanyReport/components/Management'),
  {
    resolveComponent: (component) => component.TeamList,
  }
);
const CEOCompensationChart = loadable(
  () => import('./components/CEOCompensationChart'),
  {
    ssr: false,
  }
);

const CriteriaChecks = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Introduction/components/CriteriaChecks'
    )
);
const KeyInformation = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Introduction/components/KeyInformation'
    )
);
const RecentSectionUpdates = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Introduction/components/RecentSectionUpdates'
    )
);
export type TeamType = 'management' | 'board';

export interface Member extends Omit<People, 'compensation' | 'tenure'> {
  compensation: string;
  formattedLeaderTenure: string;
  formattedBoardTenure: string;
  formattedManagementTenure: string;
}

export interface ManagementProps {
  board: Member[];
  boardAge: number;
  hasCEO: boolean;
  leaderAge: number;
  companyId: string;
  uniqueSymbol: string;
  companyName: string;
  boardTenure: number | null;
  currencyISO: string;
  reportingUnit: number;
  leaderTenure: string;
  leaderTenureFormatted: string;
  sectionNumber: number;
  managementAge: number;
  currencySymbol: string;
  leaderYearBorn: number;
  managementTeam: Member[];
  leaderLastName: string;
  leaderBiography: string;
  managementTenure: number | null;
  leaderSalutation: string;
  leaderCompensation: string;
  keyMetric: string;
  keyMetricTitle: string;
  viewMode: ViewMode;
  marketCapListing: number;
  /**
   * CEO Compensation
   */
  earnings: number[][];
  earningsMax: number;
  earningsMin: number;
  salary: number[][];
  totalCompensation: number[][];
  salaryMax: number;
  salaryMin: number;
  totalCompensationMax: number;
  totalCompensationMin: number;
  CEOCompensationDayZero?: number;
}

const Management = ({
  board,
  boardAge,
  leaderAge,
  hasCEO,
  companyId,
  companyName,
  boardTenure,
  currencyISO,
  reportingUnit,
  leaderTenureFormatted,
  sectionNumber,
  managementAge,
  currencySymbol,
  leaderYearBorn,
  managementTeam,
  leaderLastName,
  leaderBiography,
  managementTenure,
  leaderSalutation,
  leaderCompensation,
  viewMode,
  marketCapListing,
  /**
   * CEO Compensation
   */
  earnings,
  earningsMax,
  earningsMin,
  salary,
  totalCompensation,
  salaryMax,
  salaryMin,
  totalCompensationMax,
  totalCompensationMin,
  CEOCompensationDayZero,
}: ManagementProps) => {
  const { isPristine } = useVisibleSectionsContext();
  const subSectionTitleElement = isPristine ? 'h2' : 'h3';
  const ceoDetailsDownloadUrl = getCompanyInfographicDownloadUrl(
    companyId,
    ChartType.CEOCompensation
  );
  const canonicalURL = useCompanyCanonicalUrl();
  const { data } = useCompany(canonicalURL);

  const {
    ceo_compensation_salary: compensationSalary,
    ceo_compensation_total: compensationTotal,
  } = data?.data.analysis?.data.extended.data.analysis.management || {};

  const { percent_of_shares_outstanding: sharesOutstanding } =
    data?.data.analysis?.data.extended.data.raw_data.data.members.leader || {};

  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL,
  });
  const {
    IsCEOCompensationAppropriate,
    IsCEOCompensationChangeJustified,
    IsManagementTeamSeasoned,
    IsBoardSeasoned,
  } = statements || {};

  const managementStatements: StatementName[] = [
    'IsCEOCompensationAppropriate',
    'IsCEOCompensationChangeJustified',
    'IsManagementTeamSeasoned',
    'IsBoardSeasoned',
  ];
  const statementChecks = getStatementChecks(managementStatements, statements);
  const term = useGlossaryTerms();

  return (
    <ReportSection
      sectionNumber={sectionNumber}
      data-cy-id="management-section"
    >
      <SectionSchema section="management" />
      <CompanySectionTitle title="Management" />
      <Introduction
        scoreName="Management"
        criteriaChecks={
          <CriteriaChecks
            scoreName="Management"
            checks={statementChecks}
            showSnowflake={false}
          />
        }
        keyInformation={
          <KeyInformation
            direction="column"
            keyMetrics={[
              {
                title: 'Chief executive officer',
                value: `${leaderSalutation} ${leaderLastName}`,
              },
              {
                title: 'Total compensation',
                value: compensationTotal
                  ? toCurrencyTruncated(compensationTotal, 1, currencyISO)
                  : 'n/a',
                tooltip: term('total compensation'),
              },
            ]}
            metrics={[
              {
                title: 'CEO salary percentage',
                value:
                  compensationSalary && compensationTotal
                    ? formatterPercentage(
                        compensationSalary / compensationTotal
                      )
                    : 'n/a',
                tooltip: term('ceo salary percentage'),
              },
              {
                title: 'CEO tenure',
                value: leaderTenureFormatted,
                tooltip: term('ceo tenure'),
              },
              {
                title: 'CEO ownership',
                value: sharesOutstanding
                  ? `${toTruncated(sharesOutstanding, 1)}%`
                  : 'n/a',
                tooltip: term('ceo ownership'),
              },
              {
                title: 'Management average tenure',
                value: managementTenure
                  ? formatterTenure(managementTenure)
                  : 'no data',
                tooltip: term('management average tenure'),
              },
              {
                title: 'Board average tenure',
                value: boardTenure ? formatterTenure(boardTenure) : 'no data',
                tooltip: term('board average tenure'),
              },
            ]}
          />
        }
        recentSectionUpdates={
          <RecentSectionUpdates
            lookup={canonicalURL}
            title="Recent management updates"
            filterByEventCategory="management"
          />
        }
      />
      <BrazeContentCardWrapper>
        <BrazeContentCard
          feedType="company-overview.management"
          anchorTarget="_blank"
        />
      </BrazeContentCardWrapper>
      <PageAnchor type="sub-section-title" id={MANAGEMENT_CEO_SALARY} />
      {salary !== null && salary.length > 0 && earnings.length > 0 && (
        <>
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="CEO Compensation Analysis"
            body={
              <ChartWrapper
                id="management-ceo-compensation-chart"
                chart={
                  <CEOCompensationChart
                    currencyISO={currencyISO}
                    reportingUnit={reportingUnit}
                    earnings={earnings}
                    earningsMax={earningsMax}
                    earningsMin={earningsMin}
                    salary={salary}
                    salaryMax={salaryMax}
                    salaryMin={salaryMin}
                    totalCompensation={totalCompensation}
                    totalCompensationMax={totalCompensationMax}
                    totalCompensationMin={totalCompensationMin}
                    dayZero={CEOCompensationDayZero}
                  />
                }
                table={<CEOCompensationTable />}
                feedbackLabel={COMPANY_CEO_COMPENSATION}
                exportDarkUrl={ceoDetailsDownloadUrl}
                preRenderHeight={490}
                preRenderHeightMdDown={470}
                preRenderHeightSmDown={460}
              />
            }
            statements={
              <>
                <Statement
                  title={IsCEOCompensationAppropriate?.title}
                  description={
                    <Description statement={IsCEOCompensationAppropriate} />
                  }
                  value={IsCEOCompensationAppropriate?.value}
                  statementId={CompanyStatements.managementStatement1}
                />
                <Statement
                  title={IsCEOCompensationChangeJustified?.title}
                  description={
                    <Description statement={IsCEOCompensationChangeJustified} />
                  }
                  value={IsCEOCompensationChangeJustified?.value}
                  statementId={CompanyStatements.managementStatement2}
                />
              </>
            }
          />
        </>
      )}
      <hr />
      <PageAnchor type="sub-section-title" id={MANAGEMENT_CEO} />
      <ReportSubSection
        titleElement={subSectionTitleElement}
        title="CEO"
        body={
          <Leader
            hasCEO={hasCEO}
            leaderAge={leaderAge}
            companyName={companyName}
            leaderTenure={leaderTenureFormatted}
            leaderYearBorn={leaderYearBorn}
            leaderLastName={leaderLastName}
            leaderBiography={leaderBiography}
            leaderSalutation={leaderSalutation}
            leaderCompensation={leaderCompensation}
            viewMode={viewMode}
          />
        }
      />
      <PageAnchor type="sub-section-title" id={MANAGEMENT_TENURE} />
      <hr />
      {managementTenure !== null && managementTenure > 0 && (
        <>
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Leadership Team"
            trackingProps={{ subject: 'Management Team' }}
            body={
              <>
                <ListTruncator viewMode={viewMode} minHeight={200}>
                  <TeamList
                    dataCyId={'leadership-team'}
                    team={managementTeam}
                    teamType="management"
                    marketCapListing={marketCapListing}
                    currencySymbol={currencySymbol}
                    viewMode={viewMode}
                  />
                </ListTruncator>
                <TeamTenure
                  dataCyId={'leadership-team'}
                  tenure={toTruncated(managementTenure, 1)}
                  averageAge={managementAge}
                />
              </>
            }
            statements={
              <Statement
                title={IsManagementTeamSeasoned?.title}
                description={
                  <Description statement={IsManagementTeamSeasoned} />
                }
                value={IsManagementTeamSeasoned?.value}
                statementId={CompanyStatements.managementStatement3}
              />
            }
          />
          <hr />
        </>
      )}
      <PageAnchor type="sub-section-title" id={MANAGEMENT_BOARD_TENURE} />
      {boardTenure !== null && boardTenure > 0 ? (
        <>
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Board Members"
            trackingProps={{ subject: 'Board Members' }}
            body={
              <>
                <ListTruncator viewMode={viewMode} minHeight={200}>
                  <TeamList
                    dataCyId={'board-members'}
                    team={board}
                    teamType="board"
                    marketCapListing={marketCapListing}
                    currencySymbol={currencySymbol}
                    viewMode={viewMode}
                  />
                </ListTruncator>
                <TeamTenure
                  dataCyId={'board-members'}
                  tenure={toTruncated(boardTenure, 1)}
                  averageAge={boardAge}
                />
              </>
            }
            statements={
              <Statement
                title={IsBoardSeasoned?.title}
                description={<Description statement={IsBoardSeasoned} />}
                value={IsBoardSeasoned?.value}
                statementId={CompanyStatements.managementStatement4}
              />
            }
          />
          <hr />
        </>
      ) : null}
    </ReportSection>
  );
};

export default Management;
